
import Item from "../menu-user/Item.vue";
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref
} from "vue";
import { useStore } from "vuex";
import { User } from "../../../../../../domain/entities/entities";
import { RepositoryException } from "../../../../../../domain/exceptions/exceptions";
import {
  dispatchErrorBox,
  enableBodyOverflow,
  isCirculoTemplate,
  isDecolaTemplate,
  isGTopTemplate
} from "../../../mixins";
import router from "@/routes";
import MenuItem, { ItemRoute } from "./MenuItem";
import { ExternalRoute } from "../menu/MenuItem";
import { currentDomain } from "../../../../../repository/http";
import Suggestion from "../../../modals/Suggestion.vue";
import LogoutConfirm from "./LogoutConfirm.vue";

export default defineComponent({
  name: "MenuUser",
  methods: { isCirculoTemplate },
  components: { LogoutConfirm, Suggestion, Item },
  props: {
    isOpened: {
      type: Boolean,
      required: false
    }
  },

  setup() {
    const store = useStore();
    const showConfirmation = ref(false);
    const isLogoutOpen = ref(false);

    const closeSuggestionModal = () => (showConfirmation.value = false);

    const openSuggestion = () => {
      showConfirmation.value = true;
      store.dispatch("miscellany_module/setDropdownMenuState", false);
    };

    const openLogoutModal = () => {
      isLogoutOpen.value = true;
    };

    const closeLogoutModal = () => {
      isLogoutOpen.value = false;
    };

    /** User getter.
     * @see User. */
    const user: ComputedRef<User> = computed(
      () => store.getters["userProfile_module/user"]
    );

    const userPhoto: ComputedRef<string> = computed(() => {
      const user: User = store.getters["userProfile_module/user"];
      return user.image;
    });

    const configuration = computed(
      () => store.getters["configuration_module/configuration"]
    );

    const student = new MenuItem(
      "Meu perfil",
      "user.svg",
      new ItemRoute("profile", { option: "statistics" })
    );

    const events = new MenuItem(
      "Meus treinamentos",
      "",
      new ItemRoute("profile", { option: "courses" })
    );

    const tracks = new MenuItem(
      "Trilhas obrigatórias",
      "",
      new ItemRoute("profile", { option: "tracks" })
    );

    const adminPanel = new MenuItem(
      "Painel Admin",
      "",
      new ExternalRoute(currentDomain + "/admin#/dashboard", "_blank")
    );
    adminPanel.isAlwaysEnabled = true;

    const managerPanel = new MenuItem(
      "Meus times",
      "",
      new ExternalRoute(currentDomain + "/admin#/team", "_blank")
    );
    managerPanel.isAlwaysEnabled = true;

    const statement = new MenuItem("Extrato", "", new ItemRoute("statement"));

    const order = new MenuItem(
      "Histórico de pedidos",
      "",
      new ItemRoute("profile", { option: "history-order" })
    );

    const indication = new MenuItem(
      "Treinamentos recomendados",
      "",
      new ItemRoute("profile", { option: "indication" })
    );

    const favorite = new MenuItem(
      "Treinamentos favoritos",
      "",
      new ItemRoute("profile", { option: "favorite" })
    );

    const dashboard = new MenuItem(
      "Início",
      "dashboard.svg",
      new ItemRoute("dashboard")
    );

    const userDashboard = new MenuItem(
      "Meu dashboard",
      "",
      new ItemRoute("statistics", { option: "chart" })
    );

    const shop = new MenuItem("Loja", "", new ItemRoute("shop"));

    const suggestion = new MenuItem("Dê sua sugestão", "", null);
    suggestion.action = openSuggestion;

    const hasCompany = computed(() => {
      const userValue = user.value;
      return (
        Array.isArray(userValue.companies) &&
        userValue.companies.some(company => company.id > 0)
      );
    });

    const initialSection = computed(() => {
      const arrayMenu: MenuItem[] = [];
      arrayMenu.push(dashboard);
      return arrayMenu;
    });

    const adminSection = computed(() => {
      const userValue = user.value;

      const arrayMenu: MenuItem[] = [];
      if (isMobile.value && [2, 5].includes(userValue.userType)) {
        arrayMenu.push(adminPanel);
      }
      if (isMobile.value && [4].includes(userValue.userType)) {
        arrayMenu.push(managerPanel);
      }
      return arrayMenu;
    });

    const trainings = computed(() => {
      const arrayMenu: MenuItem[] = [];
      arrayMenu.push(userDashboard, tracks, events, indication, favorite);
      return arrayMenu;
    });

    const storeSection = computed(() => {
      const arrayMenu: MenuItem[] = [];
      if ((isDecolaTemplate() || isGTopTemplate()) && hasCompany.value) {
        arrayMenu.push(statement, order);
      }
      if (isMobile.value) arrayMenu.push(shop);
      return arrayMenu;
    });

    const moreSection = computed(() => {
      const arrayMenu: MenuItem[] = [];
      if (configuration.value.isSuggestionMode) arrayMenu.push(suggestion);
      return arrayMenu;
    });

    function openItem(item: MenuItem): void {
      if (!item) return;

      if (item.action) {
        item.action();
        return;
      }

      const route = item.route;
      if (!route) return;

      if (route instanceof ExternalRoute) {
        window.open(route.path, route.target);
      } else {
        router.push({
          name: route.name,
          params: route.params ?? {}
        });
      }

      store.dispatch("miscellany_module/setDropdownMenuState", false);
    }

    function disabledClass(item: MenuItem) {
      return {
        "disabled-menu-item":
          configuration.value.isMaintenanceMode && !item.isAlwaysEnabled
      };
    }

    /** Tells whether the dropdown options list is open or not. */
    const isOpened: ComputedRef<boolean> = computed(
      () => store.getters["miscellany_module/headerDropdownIsOpen"]
    );

    /** Logs the user out and directs them back to the landing page */
    async function logout(): Promise<void> {
      try {
        await store.dispatch("authentication_module/logout");
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      } finally {
        enableBodyOverflow();
        await store.dispatch("miscellany_module/setDropdownMenuState", false);
        await store.dispatch("userProfile_module/updateUser", null);
        await store.dispatch("configuration_module/get");
        await router.push({ name: "home" });
      }
    }

    const isMobile = ref(window.innerWidth <= 755);

    function updateIsMobile() {
      isMobile.value = window.innerWidth <= 755;
    }

    onMounted(() => {
      window.addEventListener("resize", updateIsMobile);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", updateIsMobile);
    });

    return {
      user,
      userPhoto,
      isOpened,
      showConfirmation,
      configuration,
      student,
      dashboard,
      trainings,
      storeSection,
      moreSection,
      initialSection,
      isMobile,
      adminSection,
      openItem,
      disabledClass,
      logout,
      closeSuggestionModal,
      openSuggestion,
      isLogoutOpen,
      openLogoutModal,
      closeLogoutModal
    };
  }
});
